import { useCallback, useMemo } from "react";
import { Button } from "devextreme-react/button";
import { IColumnProps } from "devextreme-react/data-grid";
import { IItemProps } from "devextreme-react/form";

import { journalEntryTypeOptions } from "../../model/constants/enums";
import { Account } from "../../model/model";
import { SaveChangeResult } from "../../components/dataGridActionUtil";
import { createFormulaEditCellComponent } from "../../components/formulaEditor";
import { FormulaContextKeys } from "../../model/formula";

import { CrudDataGrid } from "../common/CrudDataGrid";

import { dummyJournalEntryRow } from "./model/metaData";
import { JournalEntryTypeMapping, MappingComponentProps } from "./model/model";

const FORMULA_FIELDS = ["amountFormula", "conditionFormula"];

type JournalEntryTypesProps = MappingComponentProps & {
  accountList: Account[];
};

export const JournalEntryTypes = (props: JournalEntryTypesProps) => {
  props.logger.debug("Rendering JournalEntryTypes component");

  const httpClient = props.pageProps.appUtil.httpClients["eAccountingService"];

  // Render the formula editor through the column's `editCellComponent` so the DataGrid's editing
  // controller supplies the current value and receives changes. A form-item template would bypass
  // that binding and open with an empty editor for existing rows.
  const formulaEditCellComponent = useMemo(
    () =>
      createFormulaEditCellComponent({
        httpClient,
        contextKey: FormulaContextKeys.TessituraContext,
        rootObjects: ["TransactionInfo"],
      }),
    [httpClient],
  );

  const columnConfigs = useMemo(() => {
    const configs = new Map<string, IColumnProps>(
      Object.keys(dummyJournalEntryRow).map((field) => [field, { dataField: field }]),
    );
    FORMULA_FIELDS.forEach((field) => {
      const existing = configs.get(field) ?? { dataField: field };
      configs.set(field, { ...existing, editCellComponent: formulaEditCellComponent });
    });
    return configs;
  }, [formulaEditCellComponent]);

  const refreshData = async () => {
    return ((props.mappingData) as JournalEntryTypeMapping).journalEntryTypes;
  };

  const filterRowDataForForm = (data: any) => {
    return data;
  };

  const sendChange = async (change: {
    type: any;
    data: any;
    key: any;
  }): Promise<SaveChangeResult> => {
    switch (change.type) {    
      case "insert":
        if(change.data.amountFormula === undefined) {
          change.data.amountFormula = null;
        }
        if(change.data.conditionFormula === undefined) {
          change.data.conditionFormula = null;
        }
        //Somehow, need this line to change props.mappingData.journalEntryTypes array
        //Similar to ArmMappingDimensions and ArmMappingDimensionValues.tsx explanation
        (props.mappingData as JournalEntryTypeMapping).journalEntryTypes.push(change.data);
        break;
      case "update":
        const uindex = (props.mappingData as JournalEntryTypeMapping).journalEntryTypes
          .findIndex(item => item.accountName === change.key);
        if(uindex > -1) {
          const j = (props.mappingData as JournalEntryTypeMapping).journalEntryTypes[uindex];
          (props.mappingData as JournalEntryTypeMapping).journalEntryTypes[uindex] = {
            ...j,
            ...change.data,
          };
        }
        break;
      case "remove":
        const index = (props.mappingData as JournalEntryTypeMapping).journalEntryTypes
          .findIndex(item => item.accountName === change.key);
        if (index > -1) {
          (props.mappingData as JournalEntryTypeMapping).journalEntryTypes.splice(index, 1);
        }
        break;
      default:
        break;
    }
    return {
      data: change.data,
      keyExpr: "accountName",
    };
  };

  const editorItemOptions = useMemo(() => new Map<string, IItemProps>([
    [
      "debitCredit",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          searchEnabled: true,
        },
      },
    ],
    [
      "accountName",
      {
        editorType: "dxSelectBox",
        editorOptions: {
          searchEnabled: true,
        },
      },
    ],
  ]), []);

  const onEditorPreparing = useCallback((e: any) => {
    if (e.parentType === "filterRow" || e.parentType === "searchPanel") return;

    if (e.dataField === "debitCredit") {
      e.editorOptions.items = journalEntryTypeOptions;
    } else if (e.dataField === "accountName") {
      e.editorOptions.items = props.accountList;
      e.editorOptions.valueExpr = "accountName";
      e.editorOptions.displayExpr = "accountName";
    }
  }, [props.accountList]);

  return (
    <div>
      <div>Journal Entry Types</div>
      <CrudDataGrid
        pageProps={props.pageProps}
        logger={props.logger}
        dummyRow={dummyJournalEntryRow}
        keyExpr={"accountName"}
        formTitle="Add Value"
        formColCount={2}
        formColSpan={2}
        refreshData={refreshData}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
        filterRowDataForForm={filterRowDataForForm}
        sendChange={sendChange}
        hideColumnChooser={true}
        hideSearchPanel={true}
        hideInactiveButton={true}
        columnConfigs={columnConfigs}
        editorItemOptions={editorItemOptions}
        onEditorPreparing={onEditorPreparing}
      ></CrudDataGrid>
      <Button
        width={120}
        text="Save"
        type="success"
        stylingMode="contained"
        onClick={async () => {
          props.logger.debug("Journal Entry Types on saving: ", ((props.mappingData) as JournalEntryTypeMapping).journalEntryTypes);
          if (props.onSaving) {
            await props.onSaving(props.mappingData);
          }
          props.onClosing();
        }}
      />
      <Button
        width={120}
        text="Cancel"
        type="danger"
        stylingMode="contained"
        onClick={() => {
          props.onClosing();
        }}
      />
    </div>
  );
};
